import { EDNVal, toEDNString, toEDNStringFromSimpleObject } from "edn-data";
import * as R from "ramda";
import { queryOmegaSingleResult } from "../api";
import {
  CreateTableRequest,
  GetTableRunControlResponse,
  GetTableRunHistoryResponse,
  RunControl,
  Table,
  TableLogs,
  TableRunFrequency,
  TableRunSuccess,
  TableSpec,
  TableTrigger,
  UpdateTableInputsRequest,
  UpdateTableColumnsRequest,
  TableFile,
  SearchTableRequest,
} from "../types/table";

export const deleteTable: (config: {
  tableId: string;
}) => Promise<any> = async ({ tableId }) => {
  const ednString = toEDNString({
    map: [["table-id", tableId]],
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/delete-table ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const fetchTableCount: (config: {
  tableId: string;
  appId: string;
}) => Promise<{
  "table-id": string;
  "app-id": string;
  count: number;
}> = async ({ tableId, appId }) => {
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["app-id", appId],
    ],
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/get-table-count ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const fetchTablePage: (config: {
  "table-id": string;
  "app-id": string;
  skip: number;
  limit: number;
}) => Promise<Table> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/get-table-page ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const fetchFullTable: (config: {
  tableId?: string;
  fileId?: string;
  appId: string;
}) => Promise<TableFile> = async ({ tableId, appId, fileId }) => {
  const fileIdArg: [EDNVal, EDNVal][] = fileId ? [["file-id", fileId]] : [];
  const tableIdArg: [EDNVal, EDNVal][] = tableId ? [["table-id", tableId]] : [];
  if (!fileIdArg && !tableIdArg) {
    throw new Error("To fetch full table, must provide table or file id");
  }
  const appArg: [EDNVal, EDNVal][] = [["app-id", appId]];
  const allArgs = [...fileIdArg, ...tableIdArg, ...appArg];
  const ednString = toEDNString({ map: allArgs });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/full-table ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const runTable: (config: {
  tableId: string;
  appId: string;
}) => Promise<TableRunSuccess> = async ({ tableId, appId }) => {
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["app-id", appId],
    ],
  });
  const query = `
          (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
          (Qo.Public.Api.Table/run-table ${ednString} Response)
          (return [Response])
        `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const setTableTrigger: (config: {
  tableId: string;
  trigger: TableTrigger;
}) => Promise<TableRunSuccess> = async ({ tableId, trigger }) => {
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["trigger", trigger],
    ],
  });
  const query = `
          (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
          (Qo.Public.Api.Table/set-table-trigger ${ednString} Response)
          (return [Response])
        `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const setTableCode: (config: {
  tableId: string;
  code: string;
}) => Promise<TableRunSuccess> = async ({ tableId, code }) => {
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["code", code],
    ],
  });
  const query = `
          (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
          (Qo.Public.Api.Table/set-table-code ${ednString} Response)
          (return [Response])
        `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getTableRunHistory: (config: {
  tableId: string;
  appId: string;
}) => Promise<GetTableRunHistoryResponse> = async ({ appId, tableId }) => {
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["app-id", appId],
    ],
  });
  const query = `
          (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
          (Qo.Public.Api.Table/table-run-history ${ednString} Response)
          (return [Response])
        `;
  const response = await queryOmegaSingleResult(query).then(
    (response: GetTableRunHistoryResponse) => {
      const runs = response["table-runs"];
      const sortedRuns = R.reverse(R.sortBy(R.prop("timestamp"), runs));
      const newResponse = R.assoc("table-runs", sortedRuns, response);
      return newResponse;
    }
  );
  return response;
};

export const getTableRunControl: (config: {
  tableId: string;
  appId: string;
}) => Promise<GetTableRunControlResponse> = async ({ appId, tableId }) => {
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["app-id", appId],
    ],
  });
  const query = `
          (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
          (Qo.Public.Api.Table/table-run-control ${ednString} Response)
          (return [Response])
        `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const setTableRunControl: (config: {
  tableId: string;
  appId: string;
  runControl: RunControl;
}) => Promise<any> = async ({ appId, tableId, runControl }) => {
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["app-id", appId],
      ["run-control", runControl],
    ],
  });
  const query = `
          (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
          (Qo.Public.Api.Table/set-table-run-control ${ednString} Response)
          (return [Response])
        `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const setTableRunFrequency: (config: {
  tableId: string;
  appId: string;
  runFrequency: TableRunFrequency;
}) => Promise<any> = async ({ appId, tableId, runFrequency }) => {
  const runFrequencyEntries = Object.entries(runFrequency);
  const ednString = toEDNString({
    map: [
      ["table-id", tableId],
      ["app-id", appId],
      ["run-frequency", { map: runFrequencyEntries }],
    ],
  });
  const query = `
          (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
          (Qo.Public.Api.Table/set-table-run-frequency ${ednString} Response)
          (return [Response])
        `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getTable: (config: SearchTableRequest) => Promise<Table> = async (
  request
) => {
  const ednString = toEDNStringFromSimpleObject(request as any, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/search-table ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getTableSpec: (config: {
  appId: string;
  tableId: string;
}) => Promise<TableSpec> = async ({ appId, tableId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["table-id", tableId],
    ],
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/get-table-spec ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getTableLogs: (config: {
  appId: string;
  tableId: string;
}) => Promise<TableLogs> = async ({ appId, tableId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["table-id", tableId],
    ],
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/get-table-logs ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const createTable: (
  config: CreateTableRequest
) => Promise<TableLogs> = async ({ appId, pageId, tableName, codeType }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["page-id", pageId],
      ["table-name", tableName],
      ["code-type", codeType],
    ],
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/create-table ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const updateTableColumns: (
  config: UpdateTableColumnsRequest
) => Promise<TableLogs> = async ({ appId, tableId, columns }) => {
  const columnsEdnVals: EDNVal[] = columns.map((column) => {
    const { is_primary_key, name } = column;
    const baseValues: [EDNVal, EDNVal][] = [["name", name]];
    const isPrimaryKeyValues: [EDNVal, EDNVal][] = is_primary_key
      ? [["is_primary_key", is_primary_key]]
      : [];
    const columnVal: EDNVal = { map: [...baseValues, ...isPrimaryKeyValues] };
    return columnVal;
  });
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["table-id", tableId],
      ["columns", columnsEdnVals],
    ],
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/set-table-columns ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const updateTableInputs: (
  config: UpdateTableInputsRequest
) => Promise<TableLogs> = async ({ appId, tableId, inputs }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["table-id", tableId],
      ["inputs", inputs],
    ],
  });
  const query = `
        (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
        (Qo.Public.Api.Table/set-table-inputs ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const writeTable: (data: {
  "app-id": string;
  "file-id": string;
  data: any;
}) => Promise<any> = async (data) => {
  const ednString = toEDNStringFromSimpleObject(data as any, {
    keysAs: "string",
  });
  const query = `
      (datastore Qo.Public.Api.Table "omega/query-omega/public/api/table")
      (= Input ${ednString})
      (Qo.Public.Api.Table/write-dataframe Input Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};
